import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { EstatPasarella } from '@/api/administracio';
import { useMonitoratgeEnviaments } from '@/api/informes';
import { Icona } from '@/components/Icona';
import { useFormat } from '@/i18n/useFormat';
import { Toolbar } from '@/layout/Toolbar';
import { ErrorCarrega } from '@/pages/EstatsPagina';
import { Buit } from '@/pages/operacio/comu';
import { XipSelect } from './comu';
import { CANALS_MONITORATGE, iconaCanal, iconaPassarella } from './suport';
import './informes.css';

/** Percentils de latència: camp del DTO i color de la barra. */
const LATENCIES: ['latenciaP50' | 'latenciaP95', string][] = [
  ['latenciaP50', 'var(--blue)'],
  ['latenciaP95', 'var(--orange)'],
];

const PUNT: Record<EstatPasarella['estat'], string> = {
  OK: 'dot--green',
  DEGRADAT: 'dot--orange',
  KO: 'dot--red',
  NO_CONFIGURAT: 'dot--gray',
};

export function W46Monitoratge() {
  const { t } = useTranslation(['informes', 'comu']);
  const f = useFormat();
  const [canal, setCanal] = useState('');
  const mon = useMonitoratgeEnviaments({ canal: canal || undefined });
  const d = mon.data;
  const ms = (v?: number | null) =>
    v == null
      ? '—'
      : v >= 1000
        ? t('w46.s', { n: f.numero(v / 1000, 1) })
        : t('w46.ms', { n: Math.round(v) });
  const passarelles = d?.passarelles ?? [];
  const enviats = passarelles.reduce((s, p) => s + p.enviats, 0);
  const fallits = passarelles.reduce((s, p) => s + p.fallits, 0);
  const exit = enviats > 0 ? ((enviats - fallits) / enviats) * 100 : null;
  const estatGlobal: EstatPasarella['estat'] = passarelles.some((p) => p.estat === 'KO')
    ? 'KO'
    : passarelles.some((p) => p.estat === 'DEGRADAT')
      ? 'DEGRADAT'
      : passarelles.some((p) => p.estat === 'NO_CONFIGURAT')
        ? 'NO_CONFIGURAT'
        : 'OK';
  const maxLatencia = Math.max(1, d?.latenciaP95 ?? 0, d?.latenciaP50 ?? 0);
  const estatEnviament = (estat: string) =>
    t(`comu:estatDestinatari.${estat}`, { defaultValue: estat });

  return (
    <section className="pantalla" data-screen="W-46">
      <Toolbar titol={t('titols.monitoratge')}>
        {d ? (
          <span className="hstack gap-8">
            <i className={`dot ${PUNT[estatGlobal]}`} />
            <span className="t-footnote dim">{t(`w46.global.${estatGlobal}`)}</span>
          </span>
        ) : null}
        <XipSelect
          icona="paperplane"
          etiqueta={t('w46.canal')}
          valor={canal}
          onCanvia={setCanal}
          opcions={[
            { valor: '', etiqueta: t('w46.totsCanals') },
            ...CANALS_MONITORATGE.map((c) => ({
              valor: String(c),
              etiqueta: t(`comu:canal.${c}`),
            })),
          ]}
        />
      </Toolbar>
      <div className="content">
        {mon.isError ? (
          <ErrorCarrega onReintenta={() => void mon.refetch()} />
        ) : !d ? (
          <Buit icona="waveform">{t('comu.carregant')}</Buit>
        ) : (
          <>
            <div
              className="grid marge-16"
              style={{
                gridTemplateColumns: `repeat(${Math.min(Math.max(passarelles.length, 1), 4)}, minmax(0, 1fr))`,
              }}
            >
              {passarelles.length === 0 ? (
                <Buit icona="server">{t('comu.senseDades')}</Buit>
              ) : (
                passarelles.map((p) => {
                  const taxa = p.enviats > 0 ? ((p.enviats - p.fallits) / p.enviats) * 100 : null;
                  return (
                    <div key={p.nom} className="stat" data-estat={p.estat}>
                      <div className="stat__label">
                        <Icona nom={iconaPassarella(p.nom)} />
                        {p.nom}
                      </div>
                      <div className="stat__value">
                        {taxa == null ? '—' : f.percentatge(taxa, taxa < 100 ? 1 : 0)}
                      </div>
                      <div className={`stat__delta ${p.estat === 'OK' ? 'up' : 'down'}`}>
                        {t(`w46.estats.${p.estat}`)} ·{' '}
                        {t('w46.resum', { enviats: p.enviats, fallits: p.fallits })}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            <div className="grid grid-sidebar">
              <div className="stack" data-testid="monitoratge">
                <div className="panel">
                  <div className="panel__head">
                    <h3>{t('w46.cuaTitol')}</h3>
                    <div className="spacer" />
                    <span className={`badge ${d.cua > 50 ? 'badge--critic' : 'badge--gray'}`}>
                      <Icona nom="clock" />
                      {t('w46.pendents', { count: d.cua })}
                    </span>
                  </div>
                  <div className="panel__body">
                    <div className="kv">
                      <span className="kv__k">{t('w46.cua')}</span>
                      <span className={`kv__v${d.cua > 50 ? ' c-red' : ''}`}>
                        {f.numero(d.cua)}
                      </span>
                    </div>
                    <div className="kv">
                      <span className="kv__k">{t('w46.enviats')}</span>
                      <span className="kv__v">{f.numero(enviats)}</span>
                    </div>
                    <div className="kv">
                      <span className="kv__k">{t('w46.fallits')}</span>
                      <span className={`kv__v${fallits > 0 ? ' c-red' : ''}`}>
                        {f.numero(fallits)}
                      </span>
                    </div>
                    <div className="kv">
                      <span className="kv__k">{t('w46.exit')}</span>
                      <span className="kv__v c-green">
                        {exit == null ? '—' : f.percentatge(exit, 1)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="panel">
                  <div className="panel__head">
                    <h3>{t('w46.errors')}</h3>
                  </div>
                  {(d.errors ?? []).length === 0 ? (
                    <Buit icona="checkmark-circle">{t('w46.senseErrors')}</Buit>
                  ) : (
                    <div className="panel__body errors-enviament" data-testid="errors">
                      {(d.errors ?? []).map((e, i) => (
                        <div className="list-row" key={e.enviamentId ?? i}>
                          <Icona
                            nom={
                              e.canal === 'PUSH' || e.canal === 'PUSH_CRITICA'
                                ? 'bell-slash'
                                : 'exclamationmark-triangle'
                            }
                            className={`icon-20 ${e.estat === 'FALLIT' ? 'c-red' : 'c-orange'}`}
                          />
                          <div className="list-row__text">
                            <div className="list-row__label">
                              <Icona nom={iconaCanal(e.canal)} className="c-gray" />{' '}
                              {t(`comu:canal.${e.canal}`, { defaultValue: e.canal })} ·{' '}
                              {estatEnviament(e.estat)}
                              {e.codi ? (
                                <span className="mono t-caption dim"> · {e.codi}</span>
                              ) : null}
                            </div>
                            <div className="list-row__sub">{e.detall ?? '—'}</div>
                          </div>
                          <span className="t-caption-2 dim nowrap">{f.dataHora(e.at)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="stack">
                <div className="panel">
                  <div className="panel__head">
                    <h3>{t('w46.latencia')}</h3>
                  </div>
                  <div className="panel__body stack" style={{ gap: 12 }}>
                    {LATENCIES.map(([clau, color]) => [clau, d[clau], color] as const).map(
                      ([clau, v, color]) => (
                        <div key={clau}>
                          <div className="hstack">
                            <span className="t-footnote">
                              {t(clau === 'latenciaP50' ? 'w46.p50' : 'w46.p95')}
                            </span>
                            <div className="spacer" />
                            <span className="t-footnote strong mono">{ms(v)}</span>
                          </div>
                          <div className="progress mt-4">
                            <i
                              style={{
                                width: `${v == null ? 0 : Math.max(2, (v / maxLatencia) * 100)}%`,
                                background: color,
                              }}
                            />
                          </div>
                        </div>
                      ),
                    )}
                    <div className="t-caption-2 dim center">{t('w46.darreres24')}</div>
                  </div>
                </div>
                <div className="panel">
                  <div className="panel__head">
                    <h3>{t('w46.passarelles')}</h3>
                  </div>
                  <div className="panel__body">
                    {passarelles.length === 0 ? (
                      <Buit icona="server">{t('comu.senseDades')}</Buit>
                    ) : (
                      passarelles.map((p) => (
                        <div key={p.nom} className="kv" data-estat={p.estat}>
                          <span className="kv__k">
                            {p.nom}
                            {p.ultimError ? (
                              <div className="t-caption-2 c-red">{p.ultimError}</div>
                            ) : null}
                          </span>
                          <span className="kv__v nowrap">
                            <i className={`dot ${PUNT[p.estat]}`} style={{ marginRight: 5 }} />
                            {t(`w46.estats.${p.estat}`)}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
